import { useMemo } from 'react';
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import katex from 'katex';
import 'katex/contrib/mhchem';

/**
 * Renders an inline/block math node with KaTeX. Clicking re-prompts for the LaTeX
 * (chemistry via mhchem's \ce{...}). Read-only when the editor is not editable.
 */
export default function MathNodeView({ node, updateAttributes, editor, extension }: NodeViewProps) {
  const latex = (node.attrs.latex as string) || '';
  const display = Boolean(node.attrs.display);

  const html = useMemo(() => {
    try {
      return katex.renderToString(latex || '\\,', { displayMode: display, throwOnError: false });
    } catch {
      return latex;
    }
  }, [latex, display]);

  const onEdit = (extension.options as { onEdit?: (latex: string, apply: (l: string) => void) => void }).onEdit;

  return (
    <NodeViewWrapper
      as="span"
      className={`math-node${display ? ' math-block' : ''}`}
      title={editor.isEditable ? 'Click to edit formula' : undefined}
      onClick={() => {
        if (!editor.isEditable) return;
        if (onEdit) onEdit(latex, (next) => updateAttributes({ latex: next }));
        else {
          const next = window.prompt('Edit formula (LaTeX; use \\ce{...} for chemistry):', latex);
          if (next !== null) updateAttributes({ latex: next });
        }
      }}
    >
      <span contentEditable={false} dangerouslySetInnerHTML={{ __html: html }} />
    </NodeViewWrapper>
  );
}
